import React, { useMemo, useState } from "react";
import { useRefreshRegister } from "../../components/RefreshProvider";
import {
  useGetTopEngagedCampaignsQuery,
  useGetTopEngagedWorkflowsQuery,
} from "../../app/useGeneratedApi";
import { Tooltip, Typography } from "@mui/material";
import { formatPercent, formatNumber } from "../../utils/formatUtils";

export function useTopEngagedPanel() {
  const [tabValue, setTabValue] = useState(0);

  const {
    data: campaignsData,
    refetch: refetchCampaigns,
    isLoading: isLoadingCampaigns,
    error: campaignsError,
  } = useGetTopEngagedCampaignsQuery({
    enabled: tabValue === 0,
  });

  const {
    data: workflowsData,
    refetch: refetchWorkflows,
    isLoading: isLoadingWorkflows,
    error: workflowsError,
  } = useGetTopEngagedWorkflowsQuery({
    enabled: tabValue === 1,
  });

  useRefreshRegister(async () => {
    await Promise.all([refetchCampaigns(), refetchWorkflows()]);
  });

  const campaignRows = useMemo(() => {
    if (!campaignsData?.topEngagedCampaigns) return [];

    return campaignsData.topEngagedCampaigns.map((item) => ({
      name: (
        <Tooltip
          title={item.name}
          componentsProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  padding: 2,
                },
              },
            },
          }}
        >
          <Typography
            variant="label2R"
            color="grey.100"
            sx={{
              display: "inline-block",
              verticalAlign: "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "300px",
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>
      ),
      sentEmails: formatNumber(item.sent),
      pnRatio: `${item.pnratio}%`,
      openRate: `${item.openProgress}%`,
      clickRate: `${item.clickedProgress}%`,
      spamRate: `${item.spamProgress}%`,
      unsubscribe: `${item.unsubscribedProgress}%`,
    }));
  }, [campaignsData]);

  const workflowRows = useMemo(() => {
    if (!workflowsData?.topEngagedWorkflows) return [];

    return workflowsData.topEngagedWorkflows.map((item) => ({
      name: (
        <Tooltip
          title={item.name}
          componentsProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  padding: 2,
                },
              },
            },
          }}
        >
          <Typography
            variant="label2R"
            color="grey.100"
            sx={{
              display: "inline-block",
              verticalAlign: "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "300px",
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>
      ),
      sentEmails: formatNumber(item.sent),
      pnRatio: formatPercent(item.pnratio),
      openRate: formatPercent(item.openProgress),
      clickRate: formatPercent(item.clickedProgress),
      spamRate: formatPercent(item.spamProgress),
      unsubscribe: formatPercent(item.unsubscribedProgress),
    }));
  }, [workflowsData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return {
    tabValue,
    handleTabChange,
    campaignRows,
    workflowRows,
    isLoadingCampaigns,
    isLoadingWorkflows,
    campaignsError,
    workflowsError,
  };
}
